/**
 * The Support request timeline — Level 1.5 of the investigation.
 *
 * Sits between the business Activity steps (what the product recorded happening) and the
 * diagnosed failures (what went wrong and why). It answers a narrower question than either:
 * **what did the API actually get asked, and what did it answer?**
 *
 * Kept deliberately distinct from the Activity Log beside it. A row here is HTTP evidence, not a
 * business event — `201` means the request returned 201, and nothing more is claimed.
 *
 * DENSITY IS A REQUIREMENT, not a preference: an investigation window can hold 150 of these.
 * A compact monospace list scans; a card per request does not. Long route patterns wrap and
 * correlation ids break rather than pushing the panel sideways.
 */

import { ArrowsLeftRight, Target } from '@phosphor-icons/react';
import {
  formatRequestDuration,
  formatRequestTime,
  isReportedRequest,
  requestStatusClass,
  requestTruncationNotice,
  REQUEST_TIMELINE_EMPTY,
  type RequestTimelineEntry,
} from '@/lib/supportRequestTimeline';

export function RequestTimeline({
  requests,
  truncated,
  reportedCorrelationId,
}: {
  requests: RequestTimelineEntry[];
  truncated: boolean;
  /** From the ticket. The one request the customer actually watched fail, when they captured it. */
  reportedCorrelationId: string | null;
}) {
  const notice = requestTruncationNotice(truncated);

  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5">
        <ArrowsLeftRight className="h-3.5 w-3.5 text-plat-muted" />
        <h4 className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">
          Request timeline
        </h4>
      </div>

      {requests.length === 0 ? (
        <p className="text-sm text-plat-muted">{REQUEST_TIMELINE_EMPTY}</p>
      ) : (
        <>
          <p className="mb-2 text-xs text-plat-muted">
            What the API was asked and what it answered, oldest first. HTTP evidence only — a
            status code is not a statement about whether the underlying work succeeded.
          </p>
          <ol className="divide-y divide-plat-border rounded-lg border border-plat-border">
            {requests.map((r, i) => {
              const reported = isReportedRequest(r, reportedCorrelationId);
              const duration = formatRequestDuration(r.durationMs);
              return (
                <li
                  key={`${r.correlationId ?? 'x'}-${r.occurredAt}-${i}`}
                  className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-1.5 text-xs ${
                    reported ? 'bg-plat-info-soft/40' : ''
                  }`}
                >
                  <span className="w-[68px] shrink-0 font-mono text-plat-muted">
                    {formatRequestTime(r.occurredAt)}
                  </span>
                  <span className="w-[52px] shrink-0 font-mono font-semibold text-plat-secondary">
                    {r.method ?? '—'}
                  </span>
                  <span
                    className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 font-mono font-semibold ${requestStatusClass(
                      r.statusCode,
                    )}`}
                  >
                    {r.statusCode}
                  </span>
                  {/* Rendered EXACTLY as the API supplied it. The backend stores a route pattern
                      (/api/v1/documents/:id/preview); nothing here reconstructs a URL, re-adds a
                      query string or substitutes an id back in. `break-all` keeps a long pattern
                      inside the panel instead of widening it. */}
                  <span className="min-w-0 flex-1 break-all font-mono text-plat-fg">
                    {r.path ?? '—'}
                  </span>
                  {duration && (
                    <span className="shrink-0 tabular-nums text-plat-muted">{duration}</span>
                  )}
                  {reported && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded bg-plat-info-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-plat-info-text">
                      <Target className="h-3 w-3" weight="fill" /> Reported request
                    </span>
                  )}
                  {r.correlationId && (
                    <span className="w-full break-all font-mono text-[10px] text-plat-faint">
                      correlation: {r.correlationId}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </>
      )}

      {notice && <p className="mt-2 text-xs text-plat-warning-text">{notice}</p>}
    </div>
  );
}
